import {
  CButton,
  CButtonGroup,
  CCol,
  CContainer,
  CModal,
  CModalBody,
  CModalHeader,
  CRow,
} from "@coreui/react";
import { Can } from "src/helpers/Auth";
import { ReportType } from "src/interfaces/enums";
import {
  CreateItemButton,
  DownloadUploadDeleteButtonGroup,
} from "../forms/FormUtils";
import {
  CreateReportModal,
  CreateReportTemplateModal,
  DeleteReportModal,
  UpdateReportModal,
  UpdateReportTemplateModal,
  DeleteReportTemplateModal,
  ReportArtifactListModal,
} from "../forms/ReportModals";
import React, { useEffect, useState } from "react";

import CIcon from "@coreui/icons-react";
import DataTable from "react-data-table-component";
import PropTypes from "prop-types";
import ReactQueryLoading from "src/reusable/ReactQueryLoading";
//import { format } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";
import { useReportList } from "./ReportServices";
import "../datasets/dataset-style.scss";
import { customDataTableStyle } from "../../reusable/DataTableStyling";
import { isFastDraft } from "src/reusable/Functions";
import { Grid, Stack } from "@mui/material";

const ReportList = (props) => {
  const history = useHistory();
  const location = useLocation();
  const [navigateToReport, setNavigateToReport] = useState(null);
  const [navigateToDataset, setNavigateToDataset] = useState(null);
  const [showArtifactListModal, setShowArtifactListModal] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const {
    status: reportListStatus,
    isLoading: isReportListLoading,
    error: reportListError,
    data: reportList,
  } = useReportList(props.reportType);

  useEffect(() => {
    // If the analytic is set, navigate to it
    if (navigateToReport) {
      //console.log(`${navigateToReport.id}`);
      history.push(`/reports/${navigateToReport.id}`);
    }
  }, [navigateToReport]);

  useEffect(() => {
    // If the analytic is set, navigate to it
    if (navigateToDataset) {
      // Construct the new URL by appending the new path seg
      let newPathSegment = `datasets/${navigateToDataset.dataset_fk}/viewer`;
      const newUrl = `${location.pathname}/${newPathSegment}`;
      history.push(newUrl);
    }
  }, [navigateToDataset]);

  let reportListColumns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      maxWidth: "60px",
      minWidth: "60px",
      hide: 730,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      grow: 1,
      //maxWidth: "400px",
    },
    {
      name: "Description",
      selector: (row) => row.description,
      // selector: (row) => (
      //   <span style={{ fontSize: "75%" }}>{row.description}</span>
      // ),
      hide: 1544,
      grow: 1.5,
    },
    {
      name: "Artifact Name",
      selector: (row) => row.artifact_name,
      omit: true,
    },
    {
      name: "Dataset ID",
      selector: (row) => row.dataset_fk,
      omit: true,
    },
  ];

  if (props.includeDetails) {
    reportListColumns.push({
      name: "Create Date",
      selector: (row) =>
        new Date(row.create_date).toLocaleString({
          timeZone: "Americas/Phoenix",
        }),
      sortable: true,
      maxWidth: "190px",
      // grow: 1,
      hide: 800,
    });
    reportListColumns.push({
      name: "Created By",
      selector: (row) => row.create_user_display_name,
      sortable: true,
      grow: 1,
      hide: 1120,
    });
    reportListColumns.push({
      name: "Based on Template",

      selector: (row) => row?.template?.name,
      omit: props.reportType === ReportType.GENERATIVE,
      grow: 1,
      hide: "md",
      sortable: true,
    });
  }

  if (props.includeButtons) {
    reportListColumns.push({
      width: props.includeJustEditButtons ? "75px" : "180px",
      compact: true,

      cell: (row) => (
        <div style={{ textAlign: "right", width: "100%", marginRight: "10px" }}>
          <>
            {!props.includeJustEditButtons && (
              <CButtonGroup className="buttons-radius">
                <Can
                  perform="report:update"
                  yes={() => (
                    <DownloadUploadDeleteButtonGroup
                      id={row.id}
                      itemName="Report"
                      reportType={props.reportType}
                      row={row}
                      updateItemModal={
                        props.reportType === ReportType.ANALYTIC
                          ? UpdateReportModal
                          : UpdateReportTemplateModal
                      }
                      updateItemModalProps={{
                        reportType: props.reportType,
                        reportName: row.name,
                        reportDescription: row.description,
                        reportAllowOthersToEdit: row.allow_others_to_edit,
                      }}
                      deleteItemModal={
                        props.reportType === ReportType.ANALYTIC
                          ? DeleteReportModal
                          : DeleteReportTemplateModal
                      }
                      deleteItemModalProps={{
                        reportType: props.reportType,
                        reportName: row.name,
                      }}
                      downloadItemModalProps={{
                        reportType: props.reportType,
                        reportName: row.name,
                      }}
                      buttonSize={"sm"}
                      updateRole="report:update"
                      deleteRole="report:delete"
                      downloadRole="report:read"
                      marginLeft="10px"
                    />
                  )}
                />
              </CButtonGroup>
            )}
          </>
        </div>
      ),
    });
  }

  return (
    <Can
      perform="report:read"
      yes={() => (
        <ReactQueryLoading
          isLoading={isReportListLoading}
          status={reportListStatus}
          error={reportListError}
        >
          <ReportArtifactListModal
            isVisible={showArtifactListModal}
            row={currentRow}
            onClose={() => {
              setShowArtifactListModal(false);
              setCurrentRow(null);
            }}
          />
          <DataTable
            columns={reportListColumns}
            data={reportList}
            responsive
            highlightOnHover
            pointerOnHover
            customStyles={customDataTableStyle}
            onRowClicked={(row) => {
              if (props.reportType === ReportType.GENERATIVE) {
                setNavigateToDataset(row);
                return;
              }
              setNavigateToReport(row);
            }}
          />
        </ReactQueryLoading>
      )}
      no={() => (
        <div>
          You do not have permission to view{" "}
          {isFastDraft() ? "reports" : "Fast Draft templates"}
        </div>
      )}
    />
  );
};

ReportList.propTypes = {
  analyticId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onReportSelect: PropTypes.func,
  includeButtons: PropTypes.bool,
  includeJustEditButtons: PropTypes.bool,
  includeDetails: PropTypes.bool,
  includeSelectedLabels: PropTypes.bool,
  reportType: ReportType,
};

ReportList.defaultProps = {
  onReportSelect: () => {},
  includeButtons: true,
  includeJustEditButtons: false,
  includeDetails: true,
  includeSelectedLabels: false,
  reportType: ReportType.ANALYTIC,
};

export const ReportListModal = (props) => {
  const onSelect = (bucketId) => {
    props.onBucketSelect(bucketId);
    if (props.closeOnSelect) {
      props.onClose();
    }
  };
  return (
    <CModal
      size={"lg"}
      show={props.isVisible}
      onClose={props.onClose}
      style={{ textAlign: "left" }}
    >
      <CModalHeader>
        <CCol sm="9">
          <h3>Select a Report</h3>
        </CCol>
        <CCol sm="3" style={{ textAlign: "right" }}>
          <CButton
            id="bucket-list-close-button"
            data-attr="reports:btn:close-report-list-modal"
            color="secondary"
            onClick={props.onClose}
            size="sm"
          >
            <CIcon name="cil-x" />
          </CButton>
        </CCol>
      </CModalHeader>
      <CModalBody>
        <ReportList
          analyticId={props.analyticId}
          onReportSelect={onSelect}
          includeJustEditButtons={true}
          includeDetails={false}
          includeSelectedLabels={true}
        />
      </CModalBody>
    </CModal>
  );
};

ReportListModal.propTypes = {
  analyticId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isVisible: PropTypes.bool.isRequired,
  onBucketSelect: PropTypes.func,
  onClose: PropTypes.func,
  closeOnSelect: PropTypes.bool,
};
ReportListModal.defaultProps = {
  onClose: () => {},
  onBucketSelect: () => {},
  closeOnSelect: true,
};

const Reports = ({ hideReportButton, title, description, reportType }) => {
  return (
    <>
      <CContainer fluid style={{ paddingRight: "15px", marginTop: "1%" }}>
        <Grid container spacing={2} sx={{ mb: "1em" }}>
          <Grid item xs={7} sm={8}>
            <h1 style={{ marginBottom: "10px" }}>{title}</h1>
            {description}
            <br />
          </Grid>
          <Grid item xs={5} sm={4} alignSelf="flex-end">
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
              flexWrap={"wrap"}
              gap={1}
            >
              <div style={{ marginLeft: "1em" }} hidden={hideReportButton}>
                <Can
                  perform="report:write"
                  yes={() => (
                    <CreateItemButton
                      data-testid="rpt-btn-create-report"
                      data-attr="reports:btn:create-report"
                      itemName="Report"
                      type={reportType}
                      createItemModal={CreateReportModal}
                    />
                  )}
                  no={() => (
                    <CreateItemButton
                      data-testid="rpt-btn-create-report"
                      data-attr="reports:btn:create-report"
                      itemName="Report"
                      type={reportType}
                      createItemModal={CreateReportModal}
                      tooltipTitle="User doesn't have permissions report:write"
                      disabled={true}
                    />
                  )}
                ></Can>
              </div>
              <Can
                perform="report:write"
                yes={() => (
                  <CreateItemButton
                    data-testid="rpt-btn-create-template"
                    data-attr="reports:btn:create-template"
                    itemName="Template"
                    type={reportType}
                    createItemModal={CreateReportTemplateModal}
                  />
                )}
                no={() => (
                  <CreateItemButton
                    data-testid="rpt-btn-create-template"
                    data-attr="reports:btn:create-template"
                    itemName="Template"
                    type={reportType}
                    createItemModal={CreateReportTemplateModal}
                    tooltipTitle="User doesn't have permissions report:write"
                    disabled={true}
                  />
                )}
              />
            </Stack>
          </Grid>
        </Grid>
        <CRow>
          <CCol xs="12">
            <ReportList reportType={reportType} />
          </CCol>
        </CRow>
      </CContainer>
    </>
  );
};

Reports.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  hideReportButton: PropTypes.bool,
  reportType: ReportType,
};
Reports.defaultProps = {
  title: "Reports",
  description:
    "Reports aggregate related buckets into a printable, consumable format. Create reports that document many bucket level analyses such as for a strengths and weaknesses evaluation.",
  hideReportButton: false,
  reportType: ReportType.ANALYTIC,
};

export default Reports;
